/* ============================================================================
   UI — renderers and feedback shared by the PDP form, the size drawer and
   Quick Buy, so all three draw swatches, size tiles and label swaps the same
   way and with the same motion.
   ========================================================================== */

import { MO, play, state } from './mo.js';

const LIGHT = new Set(['white', 'cream', 'off-white', 'optic-white']); // near-white swatches get a hairline

/* ---------------------------------------------------------------- swatches */
const swatchButton = (c, checked) => `
  <button class="swatch${LIGHT.has(c.slug) ? ' swatch--light' : ''}${c.surprise ? ' swatch--surprise' : ''}" type="button" role="radio"
    aria-checked="${checked}" tabindex="${checked ? 0 : -1}" aria-label="${c.name}${c.isNew ? ', new' : ''}${c.surprise ? ', a surprise color' : ''}"
    data-color="${c.slug}" style="--swatch:${c.hex}">
    <span class="swatch__chip"></span>${c.isNew ? '<span class="swatch__badge" aria-hidden="true"></span>' : ''}
  </button>`;

/* [New PDP] Color Variants: "Black^COLOR" + optional COLOR COMPARE, then the
   CORE and LIMITED EDITION groups. `gallery: true` makes COLOR COMPARE the
   button that opens the Color Gallery drawer (its cards and Visual Color
   Compare). */
export function renderColors(root, colors, selected, { gallery = null } = {}) {
  const all = [...colors.core, ...colors.limited];
  const current = all.find((c) => c.slug === selected) || all[0];
  const group = (label, list) => list.length ? `
    <p class="pdp-colors__group">${label}</p>
    <div class="pdp-swatches" role="radiogroup" aria-label="${label} colors">
      ${list.map((c) => swatchButton(c, c.slug === current.slug)).join('')}
    </div>` : '';
  root.innerHTML = `
    <div class="pdp-colors__row">
      <p class="pdp-colors__label"><span class="pdp-colors__name" data-color-name>${current.name}</span><span class="sup sup--variant">Color</span></p>
      ${gallery ? `<button class="pdp-link" type="button" aria-haspopup="dialog" data-open-gallery data-hover-link><span class="trim">Color Compare</span></button>` : ''}
    </div>
    ${group('Core', colors.core)}
    ${group('Limited Edition', colors.limited)}`;
  fixRoving(root.querySelectorAll('.pdp-swatches'));
  return current;
}

/* ™ in Roboto Mono, like every superscript — for names set as HTML */
export const tm = (s) => String(s).replace(/™/g, '<span class="tm">™</span>');

/* A group that doesn't hold the checked radio still needs one tab stop. */
function fixRoving(groups) {
  groups.forEach((g) => {
    const radios = [...g.querySelectorAll('[role="radio"]')];
    if (radios.length && !radios.some((r) => r.tabIndex === 0)) radios[0].tabIndex = 0;
  });
}

/* Swatch / State=Unavailable (405:24720 · hover 799:115298 · selected
   799:115306): the chip keeps its colour under a 1px white diagonal, bottom
   left to top right — still selectable. `why` finishes its accessible name. */
export function markUnavailable(root, slugs, why = '') {
  const off = new Set(slugs);
  root.querySelectorAll('.swatch').forEach((s) => {
    const base = s.dataset.label || (s.dataset.label = s.getAttribute('aria-label'));
    const on = off.has(s.dataset.color);
    s.classList.toggle('swatch--unavailable', on);
    s.setAttribute('aria-label', on && why ? `${base}, ${why}` : base);
  });
}

export function checkSwatch(root, slug) {
  root.querySelectorAll('.swatch').forEach((s) => {
    const on = s.dataset.color === slug;
    s.setAttribute('aria-checked', String(on));
    s.tabIndex = on ? 0 : -1;
  });
  fixRoving(root.querySelectorAll('.pdp-swatches'));
}

/* ----------------------------------------------------------------- ghosts */
/* A colour's ghost: the real wearfigs.com render where one exists; otherwise
   the off-white ghost with the swatch hex multiplied over it, masked to the
   garment by the ghost's own alpha — a placeholder that still reads as the
   colour. Surprise Me tints with its Figma GIF instead of a hex. `chip`
   selects the 4:5 rail-chip crop. The mask is handed to CSS as an absolute
   URL in --mask rather than written in the stylesheet: a stylesheet url()
   would be fingerprinted by the build while this file still loads the plain
   path, and the two must be the same image. */
const GHOST_BASE = 'assets/ghosts/off-white.webp';
const GHOST_BASE_CHIP = 'assets/ghosts/off-white-chip.webp';
const abs = (path) => new URL(path, document.baseURI).href;

export function garmentHTML(c, { chip = false, cls = '' } = {}) {
  if (c.ghost) {
    return `<img class="garment ${cls}" src="${chip ? c.chip : c.ghost}" alt="" decoding="async" draggable="false">`;
  }
  const mods = `${c.surprise ? ' garment--surprise' : ''}${chip ? ' garment--chip' : ''}`;
  return `<span class="garment garment--tint${mods} ${cls}" style="--tint:${c.hex};--mask:url('${abs(chip ? GHOST_BASE_CHIP : GHOST_BASE)}')" aria-hidden="true">
    <img class="garment__base" src="${chip ? GHOST_BASE_CHIP : GHOST_BASE}" alt="" decoding="async" draggable="false"><span class="garment__tint"></span></span>`;
}

/* ------------------------------------------------------------- size / type */
export function renderSizes(root, sizes, selected) {
  root.innerHTML = sizes.map((s) => `
    <button class="size-tile" type="button" role="radio" aria-checked="${s === selected}"
      tabindex="${s === selected || (!selected && s === sizes[0]) ? 0 : -1}" data-size="${s}"><span>${s}</span></button>`).join('');
}

export function renderTypes(root, types, selected) {
  root.innerHTML = types.map((t) => `
    <button class="type-tile" type="button" role="radio" aria-checked="${t === selected}"
      tabindex="${t === selected ? 0 : -1}" data-type="${t}"><span>${t}</span></button>`).join('');
}

/* Variant Select, sold out (Size Drawer/Soldout 1126:86493): the tile reads
   tertiary under a diagonal rule, and stays choosable — choosing it is how
   the shopper gets to the waitlist. */
export function markSoldOut(root, predicate) {
  root.querySelectorAll('[role="radio"]').forEach((t) => {
    const out = Boolean(predicate(t));
    t.toggleAttribute('data-soldout', out);
    const span = t.querySelector('span');
    let note = t.querySelector('.u-sr');
    if (out && !note) {
      note = document.createElement('span');
      note.className = 'u-sr';
      note.textContent = ', sold out';
      span.after(note);
    } else if (!out && note) note.remove();
  });
}

export function checkTile(root, attr, value) {
  root.querySelectorAll('[role="radio"]').forEach((t) => {
    const on = t.dataset[attr] === value;
    t.setAttribute('aria-checked', String(on));
    t.tabIndex = on ? 0 : -1;
  });
}

/* Roving focus for a radio group. `selectOnArrow` follows the WAI radio
   pattern (arrows move and select); the size grid turns it off because
   choosing a size commits and closes the drawer — arrows only move focus
   there, Enter / Space choose. */
export function bindRoving(root, onSelect, { selectOnArrow = true } = {}) {
  root.addEventListener('click', (e) => {
    const r = e.target.closest('[role="radio"]');
    if (r && root.contains(r)) onSelect(r);
  });
  root.addEventListener('keydown', (e) => {
    /* only the options on screen — a collapsed rail's hidden chips are skipped */
    const radios = [...root.querySelectorAll('[role="radio"]')].filter((r) => r.getClientRects().length);
    const i = radios.indexOf(document.activeElement);
    if (i < 0) return;
    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    let n = null;
    if (e.key in keys) n = (i + keys[e.key] + radios.length) % radios.length;
    else if (e.key === 'Home') n = 0;
    else if (e.key === 'End') n = radios.length - 1;
    if (n === null) return;
    e.preventDefault();
    radios.forEach((r, k) => { r.tabIndex = k === n ? 0 : -1; });
    radios[n].focus();
    if (selectOnArrow) onSelect(radios[n]);
  });
}

/* -------------------------------------------------------------- feedback */
/* Label crossfade (06 cart drawer): out on exit/quick, swap, in on
   entrance/base — so a value never hard-swaps under the eye. */
export async function swapText(el, html) {
  if (!el) return;
  if (el.innerHTML === html) return;
  const out = play(el, { opacity: [1, 0], y: [0, -4] }, { duration: MO.quick, ease: MO.eExit });
  await out?.finished;
  el.innerHTML = html;
  play(el, { opacity: [0, 1], y: [4, 0] }, { duration: MO.base, ease: MO.eEntrance });
}

/* Button label swap — the label leaves upward and the new one rises in.
   `affirm` licenses the spring curve: add-to-bag only. */
export async function swapButtonLabel(labelEl, html, { affirm = false } = {}) {
  if (!labelEl) return;
  const out = play(labelEl, { y: ['0%', '-120%'], opacity: [1, 0] }, { duration: MO.quick, ease: MO.eExit });
  await out?.finished;
  labelEl.innerHTML = html;
  play(labelEl, { y: ['120%', '0%'], opacity: [0, 1] },
    { duration: MO.slow, ease: affirm ? MO.eSpring : MO.eEntrance });
}

export function pulse(el) {
  play(el, { scale: [1, 1.015, 1] }, { duration: MO.base * 1.25, ease: MO.eStandard });
}

export function shake(el) {
  if (state.reduced) return;
  play(el, { x: [0, -6, 6, -4, 4, 0] }, { duration: MO.slow * .75, ease: MO.eStandard });
}

const live = () => document.querySelector('[data-live]');
export function announce(msg) {
  const el = live();
  if (!el) return;
  el.textContent = '';
  requestAnimationFrame(() => { el.textContent = msg; });
}
